import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DatabaseError, DataIntegrityError } from "../auth/errors";

import {
  ChannelThreadId,
  DraftMessageId,
  SupportTicketId,
} from "../ids/definitions";
import { MessageChannel } from "../models/shopper-ai/conversation/message";

export const PolishMessageInput = Schema.Struct({
  draftText: Schema.String,
  threadId: ChannelThreadId,
  supportTicketId: SupportTicketId,
});
export type PolishMessageInput = typeof PolishMessageInput.Type;

export const PolishMessageOutput = Schema.Struct({
  polishedText: Schema.String,
});
export type PolishMessageOutput = typeof PolishMessageOutput.Type;

export const PromptMessageInput = Schema.Struct({
  prompt: Schema.String,
  threadId: ChannelThreadId,
  supportTicketId: SupportTicketId,
  // When present, the instruction is applied as an edit to this existing
  // draft text instead of writing a fresh reply from conversation context.
  draftText: Schema.optional(Schema.String),
});
export type PromptMessageInput = typeof PromptMessageInput.Type;

export const PromptMessageOutput = Schema.Struct({
  promptedText: Schema.String,
});
export type PromptMessageOutput = typeof PromptMessageOutput.Type;

const GenerateAiResponseInputBase = Schema.Struct({
  supportTicketId: SupportTicketId,
  threadId: ChannelThreadId,
  channel: MessageChannel,
});

const GenerateAiResponseInputDefault = Schema.Struct({
  ...GenerateAiResponseInputBase.fields,
  mode: Schema.Literal("default", "readonly"),
  customerMessage: Schema.String,
  fileUrls: Schema.optional(Schema.Array(Schema.String)),
  recentCustomerMessages: Schema.optional(Schema.Array(Schema.String)),
});

const GenerateAiResponseInputCopilot = Schema.Struct({
  ...GenerateAiResponseInputBase.fields,
  mode: Schema.Literal("copilot"),
});

export const GenerateAiResponseInput = Schema.Union(
  GenerateAiResponseInputDefault,
  GenerateAiResponseInputCopilot,
);
export type GenerateAiResponseInput = typeof GenerateAiResponseInput.Type;

export const GenerateAiResponseOutput = Schema.Struct({
  threadId: ChannelThreadId,
  status: Schema.Literal("completed"),
  draftId: Schema.optional(DraftMessageId),
  /**
   * AI-authored draft content. Returned so the HTTP response is the source
   * of truth for the run's output — clients can render the reply without
   * waiting for the SSE stream or refetching the draft.
   */
  content: Schema.optional(Schema.String),
});
export type GenerateAiResponseOutput = typeof GenerateAiResponseOutput.Type;

export const AiAgentGroup = HttpApiGroup.make("aiAgent")
  .add(
    endpoint.merchant
      .post("generateAiResponse", "/generate")

      .setPayload(GenerateAiResponseInput)
      .addSuccess(GenerateAiResponseOutput)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("polishMessage", "/polish")
      .setPayload(PolishMessageInput)
      .addSuccess(PolishMessageOutput)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("promptMessage", "/prompt")
      .setPayload(PromptMessageInput)
      .addSuccess(PromptMessageOutput)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api/ai-agent");
